import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { TbActivity, TbArrowBackUp, TbArrowsExchange, TbMessageCircle } from 'react-icons/tb';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { PageState } from '../components/PageState';
import { createTrainingApi } from '../training/training-api';
import { ExerciseCard } from '../training/ExerciseCard';
import { ExerciseDetail } from '../training/ExerciseDetail';
import { ExercisePicker } from '../training/ExercisePicker';
import './member.css';
import './my-routine.css';

const TITLE = 'Mi rutina';
const defaultTrainingApi = createTrainingApi();

function countLabel(count) {
  return count === 1 ? '1 ejercicio' : `${count} ejercicios`;
}

/**
 * `/mi-cuenta/rutina` (spec "Mi rutina", Task 8): the member's active routine
 * as the coach wrote it — one tab per day, one big GIF card per exercise.
 * Tapping a card opens `ExerciseDetail` (summary first, then the full catalog
 * entry with steps). "Cambiar ejercicio" opens `ExercisePicker` on the
 * alternatives of the ORIGINAL exercise (never of the member's current swap)
 * with a "Buscar" tab over the whole catalog; the chosen exercise is saved via
 * `setSubstitution` and the server's item DTO replaces the card. A swapped
 * item says "Tu versión", names the original and offers "Volver al original".
 * The client only presents DTOs: sets/reps/rest come from the server as-is.
 */
export function MyRoutinePage({ api = defaultTrainingApi }) {
  const { handleApiError } = useAuth();
  const [state, setState] = useState({ status: 'loading', routine: null, error: '' });
  const [activeDayId, setActiveDayId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [picker, setPicker] = useState(null);
  const [busyItemId, setBusyItemId] = useState(null);
  const [feedback, setFeedback] = useState({ tone: null, text: '' });
  const detailRequestRef = useRef(0);
  const mountedRef = useRef(true);
  const tabRefs = useRef({});

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const failed = useCallback((caught) => {
    const outcome = handleApiError(caught, {
      clearBusinessData: () => setState({ status: 'loading', routine: null, error: '' }),
    });
    return outcome.handled;
  }, [handleApiError]);

  const load = useCallback(async () => {
    setState({ status: 'loading', routine: null, error: '' });
    try {
      const routine = await api.getMyRoutine();
      if (!mountedRef.current) return;
      setState({ status: 'loaded', routine: routine || null, error: '' });
      const firstDay = routine && routine.days && routine.days[0];
      setActiveDayId(firstDay ? firstDay.id : null);
    } catch (caught) {
      if (!mountedRef.current || failed(caught)) return;
      setState({ status: 'error', routine: null, error: errorMessageFor(caught) });
    }
  }, [api, failed]);

  useEffect(() => {
    load();
  }, [load]);

  function replaceItem(itemId, update) {
    setState((current) => ({
      ...current,
      routine: {
        ...current.routine,
        days: current.routine.days.map((day) => ({
          ...day,
          items: day.items.map((item) => (item.id === itemId ? update(item) : item)),
        })),
      },
    }));
  }

  async function openDetail(exercise) {
    if (!exercise) return;
    const requestId = ++detailRequestRef.current;
    setDetail(exercise);
    try {
      const full = await api.getExercise(exercise.id);
      if (mountedRef.current && detailRequestRef.current === requestId && full) setDetail(full);
    } catch (caught) {
      // The summary already on screen is enough to keep the dialog useful;
      // only a lost session needs to act on the failure.
      if (mountedRef.current) failed(caught);
    }
  }

  function closeDetail() {
    detailRequestRef.current += 1;
    setDetail(null);
  }

  function openPicker(item) {
    const original = item.originalExercise || item.exercise;
    setFeedback({ tone: null, text: '' });
    setPicker({ itemId: item.id, baseExerciseId: original && original.id, mode: 'alternatives' });
  }

  async function chooseExercise(exercise) {
    const { itemId } = picker;
    setPicker(null);
    setBusyItemId(itemId);
    try {
      const updated = await api.setSubstitution(itemId, exercise.id);
      if (!mountedRef.current) return;
      replaceItem(itemId, () => updated);
      setFeedback({
        tone: 'ok',
        text: updated.substitution
          ? `Listo: ahora hacés ${updated.exercise ? updated.exercise.name : exercise.name}.`
          : 'Listo: volviste al ejercicio original.',
      });
    } catch (caught) {
      if (!mountedRef.current || failed(caught)) return;
      setFeedback({ tone: 'error', text: errorMessageFor(caught) });
    } finally {
      if (mountedRef.current) setBusyItemId(null);
    }
  }

  async function restoreOriginal(item) {
    setBusyItemId(item.id);
    setFeedback({ tone: null, text: '' });
    try {
      await api.clearSubstitution(item.id);
      if (!mountedRef.current) return;
      replaceItem(item.id, (current) => ({
        ...current,
        exercise: current.originalExercise || current.exercise,
        originalExercise: null,
        substitution: null,
      }));
      setFeedback({ tone: 'ok', text: 'Listo: volviste al ejercicio original.' });
    } catch (caught) {
      if (!mountedRef.current || failed(caught)) return;
      setFeedback({ tone: 'error', text: errorMessageFor(caught) });
    } finally {
      if (mountedRef.current) setBusyItemId(null);
    }
  }

  if (state.status === 'loading') {
    return <PageState title={TITLE} status="loading" message="Cargando tu rutina…" />;
  }

  if (state.status === 'error') {
    return (
      <PageState
        title={TITLE}
        status="error"
        message={state.error}
        action={<button type="button" className="gym-button gym-button--secondary" onClick={load}>Reintentar</button>}
      />
    );
  }

  const { routine } = state;
  const days = (routine && routine.days) || [];

  if (!routine || days.length === 0) {
    return (
      <PageState
        title={TITLE}
        status="empty"
        message={routine ? 'Tu rutina todavía no tiene días cargados' : 'Tu profesor todavía no te asignó una rutina'}
      />
    );
  }

  const activeDay = days.find((day) => day.id === activeDayId) || days[0];
  const activeIndex = days.indexOf(activeDay);

  function handleTabKeyDown(event) {
    const moves = { ArrowRight: 1, ArrowLeft: -1 };
    let nextIndex = null;
    if (event.key in moves) nextIndex = (activeIndex + moves[event.key] + days.length) % days.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = days.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const nextDay = days[nextIndex];
    setActiveDayId(nextDay.id);
    const node = tabRefs.current[nextDay.id];
    if (node) node.focus();
  }

  return (
    <section className="gym-page gym-routine" aria-labelledby="gym-page-title">
      <header className="gym-routine__hero">
        <h1 id="gym-page-title" className="gym-routine__title">
          <span className="gym-routine__title-lead">Mi</span>{' '}
          <span className="gym-routine__title-main">rutina</span>
        </h1>
        <p className="gym-routine__name">{routine.name}</p>
        {routine.notes && (
          <div className="gym-routine__notes">
            <TbMessageCircle aria-hidden="true" focusable="false" />
            <div>
              <span>Notas del profesor</span>
              <p>{routine.notes}</p>
            </div>
          </div>
        )}
      </header>

      <div
        className="gym-routine__days"
        role="tablist"
        aria-label="Días de la rutina"
        onKeyDown={handleTabKeyDown}
      >
        {days.map((day, index) => {
          const selected = day.id === activeDay.id;
          return (
            <button
              key={day.id}
              ref={(node) => { tabRefs.current[day.id] = node; }}
              type="button"
              role="tab"
              id={`gym-routine-tab-${day.id}`}
              aria-selected={selected}
              aria-controls={`gym-routine-panel-${day.id}`}
              tabIndex={selected ? 0 : -1}
              className={`gym-routine__day${selected ? ' is-active' : ''}`}
              onClick={() => setActiveDayId(day.id)}
            >
              <span className="gym-routine__day-name">{day.name || `Día ${index + 1}`}</span>
              <span className="gym-routine__day-count">{countLabel((day.items || []).length)}</span>
            </button>
          );
        })}
      </div>

      {(activeDay.items || []).length > 0 && (
        <Link
          className="gym-button gym-button--primary gym-routine__train"
          to={`/mi-cuenta/entrenar?dia=${encodeURIComponent(activeDay.id)}`}
        >
          <TbActivity aria-hidden="true" focusable="false" />
          Entrenar {activeDay.name || `Día ${days.indexOf(activeDay) + 1}`}
        </Link>
      )}

      <div className="gym-routine__feedback">
        {feedback.tone === 'error' ? (
          <p className="gym-routine__message gym-routine__message--error" role="alert">{feedback.text}</p>
        ) : (
          <p className="gym-routine__message" role="status">{feedback.text}</p>
        )}
      </div>

      <div
        role="tabpanel"
        id={`gym-routine-panel-${activeDay.id}`}
        aria-labelledby={`gym-routine-tab-${activeDay.id}`}
        className="gym-routine__panel"
      >
        {(activeDay.items || []).length === 0 ? (
          <p className="gym-routine__empty-day">Este día todavía no tiene ejercicios.</p>
        ) : (
          <ol className="gym-routine__list">
            {activeDay.items.map((item, index) => {
              const swapped = Boolean(item.substitution);
              const busy = busyItemId === item.id;
              return (
                <li key={item.id} className={`gym-routine__item${swapped ? ' gym-routine__item--swapped' : ''}`}>
                  <ExerciseCard
                    exercise={item.exercise}
                    variant="routine"
                    order={index + 1}
                    originalExerciseName={swapped && item.originalExercise ? item.originalExercise.name : null}
                    sets={item.sets}
                    reps={item.reps}
                    restSeconds={item.restSeconds}
                    note={item.note}
                    badge={swapped ? 'Tu versión' : null}
                    onOpen={() => openDetail(item.exercise)}
                    action={(
                      <div className="gym-routine__actions">
                        <div className="gym-routine__buttons">
                          <button
                            type="button"
                            className="gym-routine__action"
                            onClick={() => openPicker(item)}
                            disabled={busy}
                          >
                            <TbArrowsExchange aria-hidden="true" focusable="false" />
                            Cambiar ejercicio
                          </button>
                          {swapped && (
                            <button
                              type="button"
                              className="gym-routine__action gym-routine__action--ghost"
                              onClick={() => restoreOriginal(item)}
                              disabled={busy}
                            >
                              <TbArrowBackUp aria-hidden="true" focusable="false" />
                              Volver al original
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  />
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {detail && <ExerciseDetail exercise={detail} onClose={closeDetail} />}
      {picker && (
        <ExercisePicker
          api={api}
          mode={picker.mode}
          selectionMode="single"
          baseExerciseId={picker.baseExerciseId}
          onModeChange={(mode) => setPicker((current) => (current ? { ...current, mode } : current))}
          onSelect={chooseExercise}
          onClose={() => setPicker(null)}
        />
      )}
    </section>
  );
}
